import { useCallback, useEffect, useState } from 'react';
import { partnerErrorMessage } from './partnerError';

export interface LoadState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** Starts a fresh load. The new data replaces the old when it arrives. */
  reload: () => Promise<void>;
}

interface Settled<T> {
  key: string;
  data: T | null;
  error: string | null;
}

/**
 * Loads when the component mounts, when `key` changes (for example a route id) and on `reload()`. A request that finishes after the key changed or the component unmounted is ignored, and data
 * loaded for an earlier key is never shown for a later one. State is set only after the request settles, never synchronously inside the effect.
 */
export function useLoad<T>(load: (signal: AbortSignal) => Promise<T>, key: string, failureMessage: string): LoadState<T> {
  const [settled, setSettled] = useState<Settled<T> | null>(null);
  const [round, setRound] = useState(0);

  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    load(controller.signal).then(
      (data) => {
        if (current) setSettled({ key, data, error: null });
      },
      (error: unknown) => {
        if (current) setSettled((previous) => ({ key, data: previous?.key === key ? previous.data : null, error: partnerErrorMessage(error, failureMessage) }));
      },
    );
    return () => {
      current = false;
      controller.abort();
    };
    // `load` is a new closure on every render by design; the key and the round are what restart a load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, round, failureMessage]);

  const reload = useCallback(async () => {
    setRound((r) => r + 1);
  }, []);

  const mine = settled?.key === key ? settled : null;
  return { data: mine?.data ?? null, error: mine?.error ?? null, loading: mine === null, reload };
}
